/**
 * Notification Service
 *
 * Provides notification-related functionality using SteemWorld SDK and Steemit API.
 */

import { PrivateKey, type Operation } from '@steempro/dsteem'
import { getSteemitClient } from '../steemit/client'
import { getUnreadNotificationCount as sdkGetUnreadCount } from '@/services/steemworld'
import type { Notification, NotificationsResponse } from '@/types/notification'

const STEEMIT_API_URL = 'https://api.steemit.com'

export interface GetNotificationsParams {
  account: string
  limit?: number
  lastId?: number
}

/**
 * Fetches notifications for a user using Steemit bridge API
 *
 * Note: This uses Steemit JSON-RPC API, not SteemWorld
 */
export async function getNotifications(
  params: GetNotificationsParams
): Promise<Notification[]> {
  const { account, limit = 50, lastId } = params

  const requestParams: Record<string, unknown> = {
    account,
    limit,
  }

  if (lastId) {
    requestParams.last_id = lastId
  }

  const response = await fetch(STEEMIT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'bridge.account_notifications',
      params: requestParams,
      id: 1,
    }),
  })

  if (!response.ok) {
    throw new Error(`Notifications API error: ${response.statusText}`)
  }

  const data: NotificationsResponse = await response.json()

  if (!data.result) {
    return []
  }

  return data.result
}

/**
 * Fetches unread notification count for a user using SteemWorld SDK
 */
export async function getUnreadNotificationCount(account: string): Promise<number> {
  return sdkGetUnreadCount(account)
}

export interface MarkAsReadParams {
  account: string
  postingKey: string
  date: string
}

/**
 * Marks all notifications as read using dsteem SDK custom_json operation
 * This broadcasts a custom_json operation to the Steem blockchain
 */
export async function markAllNotificationsAsRead(
  params: MarkAsReadParams
): Promise<{ success: boolean; error?: string }> {
  const { account, postingKey, date } = params

  try {
    const client = getSteemitClient()
    const privateKey = PrivateKey.fromString(postingKey)

    // Create the custom_json operation for marking notifications as read
    const operation: Operation = [
      'custom_json',
      {
        required_auths: [],
        required_posting_auths: [account],
        id: 'notify',
        json: JSON.stringify(['setLastRead', { date }]),
      },
    ]

    const result = await client.broadcast.sendOperations([operation], privateKey)

    if (result) {
      return { success: true }
    }

    return {
      success: false,
      error: 'Failed to broadcast mark as read operation',
    }
  } catch (error) {
    console.error('Error marking notifications as read:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Get notification icon based on type
 */
export function getNotificationIcon(type: Notification['type']): string {
  const icons: Record<Notification['type'], string> = {
    vote: '👍',
    reply: '💬',
    mention: '@',
    follow: '👤',
    reblog: '🔄',
    transfer: '💰',
    reply_comment: '💬',
    unvote: '👎',
    unfollow: '👤',
  }
  return icons[type] || '🔔'
}

/**
 * Format notification message for display
 */
export function formatNotificationMessage(notification: Notification): string {
  return notification.msg
}

/**
 * Parse notification URL to get author and permlink
 */
export function parseNotificationUrl(url: string): { author: string; permlink: string } | null {
  if (!url) return null

  // URL format: @author/permlink
  const match = url.match(/^@([^/]+)\/(.+)$/)
  if (match?.[1] && match[2]) {
    return {
      author: match[1],
      permlink: match[2],
    }
  }
  return null
}
